/**
 * Old interview-style chats, read as plain text.
 *
 * Before campaign creation was form-first the agent asked structured questions
 * (select, money, date…) and the person answered them with widgets. Those chats are
 * archived now and open read-only, so all that is left of a question is what it said
 * and what was answered — no widget, no interaction. Both arrive as `JSONString`, so
 * this reads them tolerantly: anything unreadable is "no exchange" and the row falls
 * back to its own text.
 */
import { formatNumber } from "../../utils/numbers";
import type { SelectOption } from "./wire";
import { asOption, asRecord, asString, readableDate } from "./wire";

/** What an old question row shows: the prompt, and the answer once there is one. */
export type LegacyExchange = {
  prompt: string;
  answer: string | null;
};

const optionsOf = (question: Record<string, unknown>): SelectOption[] =>
  Array.isArray(question.options)
    ? question.options.map(asOption).filter((option) => option !== null)
    : [];

const answerText = (
  question: Record<string, unknown>,
  answer: Record<string, unknown>,
): string | null => {
  const options = optionsOf(question);
  const labelOf = (value: string) =>
    options.find((option) => option.value === value)?.label ?? value;

  switch (answer.kind) {
    case "FREE_TEXT":
      return asString(answer.text) || null;
    case "SINGLE_SELECT":
      return labelOf(asString(answer.value)) || null;
    case "MULTI_SELECT":
      return Array.isArray(answer.values)
        ? answer.values
            .filter((value): value is string => typeof value === "string")
            .map(labelOf)
            .join(", ") || "Ninguno"
        : null;
    case "MONEY": {
      const amount = Number(asString(answer.value));
      if (Number.isNaN(amount)) return null;
      return `${asString(question.currency)} ${formatNumber(amount)}`.trim();
    }
    case "DATE": {
      const value = asString(answer.value);
      return value ? readableDate(value) : null;
    }
    case "PROPOSAL":
      return answer.approved === true
        ? "Aprobé los contenidos propuestos."
        : asString(answer.text) || "Rechacé los contenidos propuestos.";
    default:
      return null;
  }
};

/**
 * The prompt of an old question row and, when the row carries one, the answer as the
 * text the person saw. Null for a row with no question. The caller routes FORM and
 * CONCEPT rows elsewhere (`readRow`); this reads whatever kind it is handed as an old
 * question.
 */
export const parseLegacyExchange = (
  rawQuestion: unknown,
  rawAnswer: unknown,
): LegacyExchange | null => {
  const question = asRecord(rawQuestion);
  if (!question) return null;
  const kind = asString(question.kind);
  const prompt = asString(question.prompt);
  if (!kind || !prompt) return null;

  const answer = asRecord(rawAnswer);
  return { prompt, answer: answer ? answerText(question, answer) : null };
};
